import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { format, parse, isValid } from 'date-fns';
import TableToExcel from '@linways/table-to-excel';
import download from 'downloadjs';

import Button from '../../../shared/_TBComponents/Button';
import Modal from '../../../shared/_TBComponents/Modal';
import Text from '../../../shared/_TBComponents/Text';
import { SquareCheckbox } from '../components/presentational/tables/MaxesGoalsTable';
import { withUnit } from '../utils/maxesGoalsHelpers';

const EXPORT_TABLE_ID = 'maxes-export-table';

const formatExportDate = (dateSet) => {
  const parsed = dateSet ? parse(dateSet, 'yyyy-MM-dd', new Date()) : null;
  return parsed && isValid(parsed) ? format(parsed, 'd MMMM yyyy') : (dateSet || '');
};

const formatExportValue = (row) => withUnit(row.value, row.unitAbbr) || '';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const ExportBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ExportCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  cursor: pointer;
`;

// Used only for Excel export
const HiddenTable = styled.table`
  display: none;
`;

const ExportMaxesModal = ({
  isOpen, rows, tableMode, caption, scopeLabel, exerciseLabel, onClose,
}) => {
  const [includeEmptyRows, setIncludeEmptyRows] = useState(false);

  const exportRows = includeEmptyRows ? rows : rows.filter((row) => row.value);
  const isAthleteMode = tableMode === 'athlete';
  const getRowName = (row) => (
    isAthleteMode ? `${row.first || ''} ${row.last || ''}`.trim() : (row.exerciseName || '')
  );

  const exportNameSuffix = [scopeLabel, exerciseLabel]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const getExportFileName = (ext) => {
    const date = format(new Date(), 'yyyy-MM-dd');
    return `maxes_export${exportNameSuffix ? `_${exportNameSuffix}` : ''}_${date}.${ext}`;
  };

  const exportToExcel = (ext) => {
    const table = document.getElementById(EXPORT_TABLE_ID);
    TableToExcel.convert(table, { name: getExportFileName(ext), sheet: { name: 'Maxes' } });
  };

  // The first row repeats the scope and exercise since the file loses context
  const exportToCSV = () => {
    const headers = [
      isAthleteMode ? 'Athlete' : 'Exercise', 'Current Max', 'Date Set', 'Set From', 'Progress',
    ];
    const csvRows = exportRows.map((row) => [
      getRowName(row),
      formatExportValue(row),
      formatExportDate(row.dateSet),
      row.setFromLabel || '',
      row.goalPct ? `${row.goalPct}%` : '',
    ]);
    const dataRows = [headers, ...csvRows];
    const csv = (caption ? [[caption], [], ...dataRows] : dataRows)
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    download(csv, getExportFileName('csv'), 'text/csv');
  };

  const fileButtons = [
    { name: 'XLSX', onClick: () => exportToExcel('xlsx') },
    { name: 'XLS', onClick: () => exportToExcel('xls') },
    { name: 'CSV', onClick: exportToCSV },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Export Data
          </Text>
          <Text styledWrapperProps={{ marginBottom: '24px' }}>
            Export the currently visible list by clicking a file type below.
          </Text>
          <ExportCheckboxRow onClick={() => setIncludeEmptyRows(!includeEmptyRows)}>
            <SquareCheckbox
              checked={includeEmptyRows}
              onChange={(e) => setIncludeEmptyRows(e.target.checked)}
            />
            <Text styledTextProps={{ fontSize: '14px', color: '#1A1A1A' }}>
              Include rows with no recorded max
            </Text>
          </ExportCheckboxRow>
          <ExportBody>
            {fileButtons.map((fileButton) => (
              <Button
                key={fileButton.name}
                name={fileButton.name}
                fullWidth
                bgColor='#444444'
                borderColor='#444444'
                txtColor='#FFF'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                onClick={fileButton.onClick}
              />
            ))}
          </ExportBody>
        </ContentWrapper>
      </Modal>

      <HiddenTable id={EXPORT_TABLE_ID}>
        <thead>
          {caption && (
            <tr>
              <th colSpan={5}>{caption}</th>
            </tr>
          )}
          <tr>
            <th>{isAthleteMode ? 'Athlete' : 'Exercise'}</th>
            <th>Current Max</th>
            <th>Date Set</th>
            <th>Set From</th>
            <th>Progress</th>
          </tr>
        </thead>
        <tbody>
          {exportRows.map((row) => (
            <tr key={isAthleteMode ? row.userId : row.exerciseId}>
              <td>{getRowName(row)}</td>
              <td>{formatExportValue(row)}</td>
              <td>{formatExportDate(row.dateSet)}</td>
              <td>{row.setFromLabel || ''}</td>
              <td>{row.goalPct ? `${row.goalPct}%` : ''}</td>
            </tr>
          ))}
        </tbody>
      </HiddenTable>
    </>
  );
};

ExportMaxesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({
    userId: PropTypes.number,
    exerciseId: PropTypes.number,
    first: PropTypes.string,
    last: PropTypes.string,
    exerciseName: PropTypes.string,
    value: PropTypes.string,
    unitAbbr: PropTypes.string,
    dateSet: PropTypes.string,
    setFromLabel: PropTypes.string,
    goalPct: PropTypes.number,
  })),
  tableMode: PropTypes.oneOf(['athlete', 'exercise']).isRequired,
  caption: PropTypes.string,
  scopeLabel: PropTypes.string,
  exerciseLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ExportMaxesModal.defaultProps = {
  rows: [],
  caption: null,
  scopeLabel: null,
  exerciseLabel: null,
};

export default ExportMaxesModal;
